import React, { useCallback, useMemo } from "react";
import { AttributeTitle } from "components/attributes/compare-table/attribute-title";
import { useColumnTemplate } from "shared/hooks/use-column-template";
import ReadOnlyField from "components/attributes/compare-table/read-only-field";

export const ProductCompareAttributeRow = ({ attrGroups, references, columnWidths, productMatches }) => {
    const gridTemplate = useColumnTemplate(references.length + productMatches.length + 1, columnWidths, false);
    const mergedTooltip = useMemo(() => {
        return attrGroups
            .map(({ tooltip }) => tooltip)
            .filter((tooltip) => tooltip)
            .join(", ");
    }, [attrGroups]);

    const checkMatch = useCallback(
        (matchedUuids) => {
            // if there are multiple groups, all of them need to match to be considered a match, otherwise just one match is enough
            if (attrGroups.length > 1) {
                return attrGroups.every((attrGroup) => {
                    return matchedUuids?.some((matchedUuid) => matchedUuid.toString() === attrGroup.uuid);
                });
            }

            return attrGroups.some((attrGroup) => {
                return matchedUuids?.some((matchedUuid) => matchedUuid.toString() === attrGroup.uuid);
            });
        },
        [attrGroups]
    );

    const extractValue = useCallback((value) => {
        if (value && typeof value === "object" && "text" in value) {
            return value.text;
        }

        return value;
    }, []);

    const resolveValue = useCallback(
        (match, group, groups) => {
            if (groups.length > 1) {
                const first = extractValue(match.attributes[groups[0].uuid]);
                const second = extractValue(match.attributes[groups[1].uuid]);

                if (first === undefined && second === undefined) {
                    return "";
                }

                return `${first || ""} ${second || ""}`.trim();
            }

            return match.attributes[group.uuid] || "";
        },
        [extractValue]
    );

    const renderAttributeFields = useCallback(
        (dataSource, extraProps = {}) =>
            attrGroups.map((group, index, groups) => {
                // display only the first group and merge value from the 2nd
                if (groups.length > 1 && index > 0) {
                    return null;
                }

                return (
                    <ReadOnlyField
                        key={`AIF_${group.uuid}_${dataSource.codingWipKey}_ref${index}`}
                        id={group.uuid}
                        attribute={group}
                        value={resolveValue(dataSource, group, groups)}
                        defaultValue={group.defaultValue}
                        {...extraProps}
                    />
                );
            }),
        [attrGroups, resolveValue]
    );

    const [firstAttribute] = attrGroups;

    return (
        <div
            id={firstAttribute.key}
            className="product-compare-row"
            style={gridTemplate}
        >
            <AttributeTitle
                tooltip={mergedTooltip}
                title={firstAttribute.name}
                attr={firstAttribute}
                isCompleted={true}
            />
            {references.map((reference) => (
                <React.Fragment key={`RefGroup_${reference.codingWipKey}`}>{renderAttributeFields(reference, { grayed: true })}</React.Fragment>
            ))}
            {productMatches.map((match) => (
                <div
                    key={`MatchGroup_${match.codingWipKey}`}
                    className="product-compare-row -inner"
                    style={{ gridTemplateColumns: `repeat(1, 1fr)` }}
                >
                    {renderAttributeFields(match, {
                        matched: checkMatch(match.referenceProductAttributeValueMatchingUuids),
                    })}
                </div>
            ))}
        </div>
    );
};
